/**
 * Role-based access control (RBAC) for the L+TV administration dashboard.
 *
 * A single source of truth that maps each staff role to the set of admin
 * routes (path prefixes) it is allowed to open. The Sidebar and RequireAuth
 * guard both read from here, so hiding a menu item and blocking the route
 * always stay in sync.
 */

export type Role =
  | "SUPER ADMIN"
  | "ADMIN"
  | "EDITOR"
  | "AUTHOR"
  | "VIDEO MANAGER"
  | "PROGRAM MANAGER"
  | "TECHNICAL ADMIN"
  | "SOCIAL MEDIA MANAGER"
  | "MODERATOR";

export const ALL_ROLES: Role[] = [
  "SUPER ADMIN",
  "ADMIN",
  "EDITOR",
  "AUTHOR",
  "VIDEO MANAGER",
  "PROGRAM MANAGER",
  "TECHNICAL ADMIN",
  "SOCIAL MEDIA MANAGER",
  "MODERATOR",
];

/** Roles that are allowed to create/edit/remove other staff members. */
export const USER_MANAGER_ROLES: Role[] = ["SUPER ADMIN", "ADMIN"];

const DASHBOARD = "/admin/dashboard";
const HELP = "/admin/help";

/** Allowed admin path prefixes per role. "*" means unrestricted access. */
const ROLE_PATHS: Record<Role, string[]> = {
  "SUPER ADMIN": ["*"],
  ADMIN: ["*"],
  // Editor — content editing across the site, no settings / no user management.
  EDITOR: [
    DASHBOARD,
    "/admin/news",
    "/admin/videos",
    "/admin/gallery",
    "/admin/media",
    "/admin/programs",
    "/admin/schedule",
    "/admin/breaking",
    "/admin/homepage",
    "/admin/pages",
    "/admin/adverts",
    "/admin/entertainment",
    "/admin/sports",
    "/admin/religion",
    "/admin/photo-studio",
    "/admin/production",
    HELP,
  ],
  // Author — write news and breaking headlines only.
  AUTHOR: [DASHBOARD, "/admin/news", "/admin/breaking", HELP],
  // Video Manager — videos, gallery and the media library.
  "VIDEO MANAGER": [DASHBOARD, "/admin/videos", "/admin/gallery", "/admin/media", HELP],
  // Program Manager — programs and the schedule.
  "PROGRAM MANAGER": [DASHBOARD, "/admin/programs", "/admin/schedule", HELP],
  // Technical Admin — broadcasting / live control.
  "TECHNICAL ADMIN": [DASHBOARD, "/admin/live", "/admin/direct", HELP],
  // Social Media Manager — social links and messages.
  "SOCIAL MEDIA MANAGER": [DASHBOARD, "/admin/social", "/admin/messages", HELP],
  // Moderator — read/respond to messages only.
  MODERATOR: [DASHBOARD, "/admin/messages", HELP],
};

/** Whether a given role may open the given admin path. */
export function canAccess(role: Role | undefined | null, path: string): boolean {
  if (!role) return false;
  const allowed = ROLE_PATHS[role];
  if (!allowed) return false;
  if (allowed.includes("*")) return true;
  return allowed.some((p) => path === p || path.startsWith(`${p}/`));
}

/** Whether a role may manage other staff (create/edit/remove users). */
export function canManageUsers(role: Role | undefined | null): boolean {
  return role === "SUPER ADMIN" || role === "ADMIN";
}

/**
 * The roles an actor is allowed to assign when creating/editing a user.
 * Admins can assign everything except SUPER ADMIN; only a Super Admin can
 * create another Super Admin.
 */
export function assignableRoles(actor: Role | undefined | null): Role[] {
  if (actor === "SUPER ADMIN") return ALL_ROLES;
  if (actor === "ADMIN") return ALL_ROLES.filter((r) => r !== "SUPER ADMIN");
  return [];
}